import React, { useState } from "react";

import type { AdminModerationReview, ReviewReportRecord } from "@/types/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import {
  Check,
  ExternalLink,
  Trash2
} from "lucide-react";

interface ReportedReviewsTableProps {
  reports: ReviewReportRecord[];
  reviews: AdminModerationReview[];
  onResolveReport: (reportId: string) => void;
  onVisibilityToggle: (reviewId: string, newVisibility: boolean) => Promise<void>;
  onDeleteReview: (reviewId: string) => void;
  isProcessing: boolean;
}

const ReportedReviewsTable = ({
  reports,
  reviews,
  onResolveReport,
  onVisibilityToggle,
  onDeleteReview,
  isProcessing
}: ReportedReviewsTableProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const reviewMap = new Map(reviews.map(review => [review.id, review]));

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const handleVisibilityToggle = (reviewId: string, currentIsPublic: boolean) => {
    const newVisibility = !currentIsPublic;
    onVisibilityToggle(reviewId, newVisibility).catch(error => {
      console.error("Error toggling visibility:", error);
    });
  };

  const handleResolveReport = async (reportId: string) => {
    try {
      await onResolveReport(reportId);
    } catch (error) {
      console.error("Error resolving report:", error);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    try {
      await onDeleteReview(reviewId);
    } catch (error) {
      console.error("Error deleting review:", error);
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[20%]">Reported By</TableHead>
            <TableHead className="w-[25%]">Review Info</TableHead>
            <TableHead className="w-[20%]">Report Reason</TableHead>
            <TableHead className="w-[10%]">Date</TableHead>
            <TableHead className="w-[5%]">Status</TableHead>
            <TableHead className="w-[20%] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reports.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-6 text-gray-500">
                No reported reviews found
              </TableCell>
            </TableRow>
          ) : (
            reports.map((report) => {
              const review = report.reviewId ? reviewMap.get(report.reviewId) : undefined;
              const isExpanded = expandedRow === report.id;

              return (
                <React.Fragment key={report.id}>
                  <TableRow
                    className={`cursor-pointer ${report.resolved ? "bg-gray-50" : ""}`}
                    onClick={() => toggleRowExpansion(report.id)}
                  >
                    <TableCell className="font-medium">
                      {report.name || "Anonymous"}
                      <div className="text-xs text-gray-500 mt-1">{report.email}</div>
                    </TableCell>
                    <TableCell>
                      {review ? (
                        <div>
                          <div className="font-medium">{review.userName}</div>
                          <div className="text-xs text-gray-500 mt-1">Rating: {review.rating}/5</div>
                        </div>
                      ) : (
                        <div className="text-gray-500 italic">Review not found</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate">
                        {report.reportReason || "No reason provided"}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(report.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {report.resolved ? (
                        <Badge variant="default" className="bg-green-100 text-green-800">
                          Resolved
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        {review && (
                          <Badge
                            variant={review.isPublic ? "default" : "secondary"}
                            className={`cursor-pointer ${
                              review.isPublic 
                                ? "bg-green-100 text-green-800 hover:bg-green-200" 
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVisibilityToggle(review.id, review.isPublic);
                            }}
                          >
                            {review.isPublic ? "Public" : "Hidden"}
                          </Badge>
                        )}
                        
                        {review && review.providerId && (
                          <Button
                            variant="outline" 
                            size="sm"
                            asChild
                            className="text-whatsapp border-whatsapp/20 hover:bg-whatsapp-light hover:text-whatsapp"
                          >
                            <Link to={`/service/${review.providerId}`} target="_blank">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View
                            </Link>
                          </Button>
                        )}
                        
                        {review && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                                disabled={isProcessing}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Review</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this review? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteReview(review.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        
                        {!report.resolved && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResolveReport(report.id);
                            }}
                            disabled={isProcessing}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                          >
                            <Check className="h-4 w-4 mr-1" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-gray-50">
                        <div className="p-4">
                          <h4 className="font-medium">Report Details</h4>
                          <p className="text-sm whitespace-pre-line">
                            {report.message || "No additional details provided."}
                          </p>
                          {review?.comment && (
                            <>
                              <h4 className="font-medium mt-4">Review Comment</h4>
                              <p className="text-sm whitespace-pre-line">{review.comment}</p>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReportedReviewsTable;
